/**
 * Validates the authored document and turns Zod's issues into editor field paths.
 *
 *  - It NEVER THROWS. Throwing is the player's contract; the editor's job is to catch, render a
 *    summary and refuse to save — hence `safeParse` and a returned result.
 *  - It NEVER WATCHES the document. Validation runs on save and on demand only: a half-built screen
 *    is legitimately invalid, so per-keystroke validation would mean a permanently red editor.
 *
 * Paths are document-rooted and dotted, e.g.
 * `definition.screens.2.activities.0.interaction.options`. One convention, no special cases.
 */
import { computed, ref, toValue } from 'vue'

import type { MaybeRefOrGetter } from 'vue'
import type { AssessmentValidationIssue, UseValidateDefinitionReturn } from './interfaces'
import type { AssessmentValidationResult } from './types'

import { withCount } from '@shared/lib/format'

import { AssessmentTemplateDocumentSchema } from '../../lib/assessments/schema'
import { normalizeAssessmentDocument } from '../../lib/assessments/normalize-document'

/** Zod path segments are `PropertyKey`s. Numeric indices stay numbers in `segments` and become
 *  plain digits in `path`. A symbol key cannot occur in JSON, but is stringified rather than
 *  dropped, so a path is never silently truncated to the wrong field. */
const toSegments = (path: readonly PropertyKey[]): (string | number)[] =>
  path.map(segment => (typeof segment === 'number' ? segment : String(segment)))

const toIssue = (issue: { path: readonly PropertyKey[], message: string }): AssessmentValidationIssue => {
  const segments = toSegments(issue.path)
  return {
    segments,
    path: segments.join('.'),
    message: issue.message,
  }
}

/**
 * @param document the working document. Reactive in (a ref, computed or getter) per
 *   `composables.md`; read only when `validate()` is called.
 */
export const useValidateDefinition = (
  document: MaybeRefOrGetter<unknown>,
): UseValidateDefinitionReturn => {
  const issues = ref<AssessmentValidationIssue[]>([])
  const hasValidated = ref<boolean>(false)

  const isValid = computed<boolean>(() => hasValidated.value && issues.value.length === 0)

  const canSave = computed<boolean>(() => isValid.value)

  const summary = computed<string | null>(() => {
    if (!hasValidated.value) {
      return null
    }
    const count = issues.value.length
    if (count === 0) {
      return 'No problems found'
    }
    return `${withCount(count, { one: 'problem', other: 'problems' })} found`
  })

  const validate = (): AssessmentValidationResult => {
    // Normalised defensively as well as at the API boundary (B7): a document that never went
    // through T07's loader — a freshly built one, or a test — must still parse.
    const raw = normalizeAssessmentDocument(toValue(document))
    const parsed = AssessmentTemplateDocumentSchema.safeParse(raw)

    hasValidated.value = true

    if (parsed.success) {
      issues.value = []
      return { valid: true, document: parsed.data, issues: [] }
    }

    issues.value = parsed.error.issues.map(toIssue)
    return { valid: false, document: null, issues: issues.value }
  }

  const issuesForPath = (path: string): AssessmentValidationIssue[] =>
    issues.value.filter(issue => issue.path === path || issue.path.startsWith(`${path}.`))

  const reset = (): void => {
    issues.value = []
    hasValidated.value = false
  }

  return {
    issues,
    hasValidated,
    isValid,
    summary,
    canSave,
    validate,
    issuesForPath,
    reset,
  }
}
